// On iOS, call media runs in native WebRTC (ios/App/App/NativeCallPlugin.swift)
// instead of WKWebView: WebKit forces call audio to the loudspeaker and the app
// can't change it. NativePeerConnection mirrors the small part of the
// RTCPeerConnection API that CallContext uses, so the call flow and signaling
// are the same on every platform.
//
// Video is captured and rendered natively too. The views sit behind a
// transparent WebView; setNativeVideoLayout tells native where the call UI
// wants them.

import { Capacitor, registerPlugin } from '@capacitor/core';

const NativeCall = registerPlugin('NativeCall');

export const usesNativeCallMedia = () => Capacitor.getPlatform() === 'ios';

/** Ask for microphone access up front (rejects with code 'mic_denied'). */
export const requestNativeMicrophone = () => NativeCall.requestMicrophone();

/** Ask for camera access (rejects with code 'camera_denied'). */
export const requestNativeCamera = () => NativeCall.requestCamera();

export const setNativeMuted = (muted) => NativeCall.setMuted({ muted });

/** Add the camera to a running call; renegotiate afterwards. */
export const startNativeVideo = () => NativeCall.startVideo();

/** Camera off stops capture; the other side gets black frames. */
export const setNativeCameraEnabled = (enabled) => NativeCall.setCameraEnabled({ enabled });

/** Flip front/back. Resolves { facing: 'user' | 'environment' }. */
export const switchNativeCamera = () => NativeCall.switchCamera();

/**
 * Position the native video views, in CSS pixels.
 * @param {{ remote: DOMRect|null, local: DOMRect|null, mirrorLocal?: boolean }} layout
 */
export const setNativeVideoLayout = ({ remote = null, local = null, mirrorLocal = true }) => {
  const plain = (r) => (r ? { x: r.x, y: r.y, width: r.width, height: r.height } : null);
  return NativeCall.setVideoLayout({ remote: plain(remote), local: plain(local), mirrorLocal })
    .catch((err) => console.warn('Video layout failed', err));
};

/** Subscribe to the other side's video track appearing/disappearing. */
export const onNativeRemoteVideo = (callback) => {
  const handle = NativeCall.addListener('remoteVideo', ({ available }) => callback(!!available));
  return () => Promise.resolve(handle).then((h) => h.remove()).catch(() => {});
};

export class NativePeerConnection {
  constructor({ iceServers = [], video = false } = {}) {
    this.connectionState = 'new';
    this.iceConnectionState = 'new';
    this.localDescription = null;
    this.remoteDescription = null;
    this.onicecandidate = null;
    this.onconnectionstatechange = null;
    this.oniceconnectionstatechange = null;
    this.closed = false;

    // Listeners are attached before the native peer exists, so no early
    // candidate or state event is missed
    this.listeners = [
      NativeCall.addListener('iceCandidate', (candidate) => {
        if (!this.closed) this.onicecandidate?.({ candidate });
      }),
      NativeCall.addListener('connectionState', ({ state }) => {
        if (this.closed) return;
        this.connectionState = state;
        this.onconnectionstatechange?.();
      }),
      NativeCall.addListener('iceConnectionState', ({ state }) => {
        if (this.closed) return;
        this.iceConnectionState = state;
        this.oniceconnectionstatechange?.();
      }),
    ];
    // Resolves { video } — whether the camera actually started
    this.ready = NativeCall.createPeer({ iceServers, video });
  }

  // Native creates the description and applies it locally in one step
  async createOffer() {
    await this.ready;
    return NativeCall.createOffer();
  }

  async createAnswer() {
    await this.ready;
    return NativeCall.createAnswer();
  }

  async setLocalDescription(description) {
    this.localDescription = description;
  }

  async setRemoteDescription(description) {
    await this.ready;
    await NativeCall.setRemoteDescription({ type: description.type, sdp: description.sdp });
    this.remoteDescription = description;
  }

  async addIceCandidate(candidate) {
    await this.ready;
    await NativeCall.addIceCandidate({
      candidate: candidate.candidate,
      sdpMid: candidate.sdpMid,
      sdpMLineIndex: candidate.sdpMLineIndex,
    });
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    this.connectionState = 'closed';
    this.listeners.forEach((handle) => Promise.resolve(handle).then((h) => h.remove()).catch(() => {}));
    NativeCall.close().catch(() => {});
  }
}
